import { EmptyState } from '@/components/patterns/empty-state'
import { InfoNotice } from '@/components/patterns/info-notice'
import { AdminHeaderActionButton, AdminPageHeader } from '@/components/patterns/admin-page'
import ReportDateRangeFilterBar from '@/components/reports/ReportDateRangeFilterBar'
import {
  getIndexPaginationMeta,
  IndexDataTable,
  IndexTablePagination,
} from '@/components/tables/IndexTable'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Typography } from '@/components/ui/typography'
import { usePathHelper } from '@/hooks/usePathHelper'
import { Can } from '@/lib/casl'
import {
  REPORT_DATE_MODE,
  getCurrentReportDateFilters,
  getOrganizationReportDateBounds,
} from '@/lib/report-date-range'
import { getOrganizationNow } from '@/lib/reporting-dates'
import {
  depositQueries,
  useDepositCurrentBalance,
  useDepositsReport,
  useExportDepositsReportMutation,
} from '@/lib/services/deposits/deposits.query'
import { prefetchIndexTableRouteQuery } from '@/lib/services/index-table-query-options'
import {
  DEPOSIT_STATUS,
  depositsReportSearchSchema,
  getDisplayedDepositsReportFilters,
  getDepositsReportFiltersFromSearch,
} from '@/lib/services/deposits/deposits.schema'
import {
  organizationQueries,
  useOrganization,
} from '@/lib/services/organizations/organizations.query'
import { createFileRoute, Link, notFound, useNavigate } from '@tanstack/react-router'
import { Download, Landmark } from 'lucide-react'
import { startTransition } from 'react'
import { toast } from 'sonner'
import { downloadExportedReport, getExportErrorMessage } from '../-helpers'
import { DepositPipelineCard } from './-pipeline-card'
import { DEPOSIT_STATUS_OPTIONS, getDepositTotalRows, getOtherActivityMinor } from './-presentation'
import { getDepositColumns } from './-table-columns'

export const Route = createFileRoute('/_authenticated/$orgId/reports/deposits/')({
  component: DepositsReportPage,
  validateSearch: (search) => depositsReportSearchSchema.parse(search),
  loaderDeps: ({ search }) => ({ search }),
  loader: async ({ context, deps: { search }, params }) => {
    if (!context.ability.can('view', 'payouts')) throw notFound()

    const organization =
      context.queryClient.getQueryData?.(organizationQueries.show(params.orgId).queryKey) ??
      (await context.queryClient.ensureQueryData(organizationQueries.show(params.orgId)))
    const filters = getReportFilters(search, organization?.timezone)

    void prefetchIndexTableRouteQuery(
      context.queryClient,
      depositQueries.report({ organizationId: params.orgId, filters })
    )
    void context.queryClient.prefetchQuery(
      depositQueries.currentBalance({ organizationId: params.orgId })
    )
  },
})

export function DepositsReportPage() {
  const navigate = useNavigate()
  const path = usePathHelper()
  const { orgId } = Route.useParams()
  const search = Route.useSearch()
  const { data: organization } = useOrganization(orgId)
  const requestedFilters = getReportFilters(search, organization?.timezone)
  const { data: report, isFetching, isPlaceholderData } = useDepositsReport(requestedFilters)
  const { data: currentBalance } = useDepositCurrentBalance()
  const exportMutation = useExportDepositsReportMutation()
  const filters = getDisplayedDepositsReportFilters(
    requestedFilters,
    report?.filters,
    isPlaceholderData
  )
  const rows = report?.rows ?? []
  const meta = getIndexPaginationMeta(report, filters.page ?? 1, filters.page_size ?? 20)
  const organizationNow = getOrganizationNow(organization?.timezone)
  const organizationDateBounds = getOrganizationReportDateBounds(
    organization?.created_at,
    organizationNow,
    organization?.timezone
  )
  // Expected bank dates can be in the future, so this report intentionally has no maximum date.
  const datePickerBounds = { minDate: organizationDateBounds.minDate }
  // The period total closes the table; it covers completed deposits on every page.
  const tableRows = rows.length
    ? [...rows, ...getDepositTotalRows(report?.summary?.currencies)]
    : []
  const reportPath = path.deposits.index().using({ orgId }).link

  const updateFilters = (changes) => {
    startTransition(() => {
      void navigate({
        ...reportPath,
        search: {
          start_date: filters.start_date,
          end_date: filters.end_date,
          date_mode: filters.date_mode ?? REPORT_DATE_MODE.CUSTOM,
          status: filters.status === DEPOSIT_STATUS.ALL ? undefined : filters.status,
          sales_order_id: filters.sales_order_id ?? undefined,
          page: filters.page ?? 1,
          page_size: filters.page_size ?? 20,
          ...changes,
        },
        replace: true,
      })
    })
  }

  const clearOrderFilter = () => {
    const defaults = getCurrentReportDateFilters(organizationNow)

    updateFilters({
      ...defaults,
      sales_order_id: undefined,
      page: 1,
    })
  }

  const handleExport = async () => {
    try {
      const response = await exportMutation.mutateAsync({
        organizationId: orgId,
        filters: requestedFilters,
      })

      downloadExportedReport(
        response,
        `credit-card-deposit-report-${filters.start_date}-to-${filters.end_date}.xlsx`
      )
    } catch (error) {
      toast.error(await getExportErrorMessage(error, 'Unable to export deposits right now.'))
    }
  }

  return (
    <div className="space-y-4 pb-8">
      <AdminPageHeader
        icon={Landmark}
        title="Credit Card Deposit Report"
        description="Card collections, fees, and the amount deposited to your bank."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <ReportDateRangeFilterBar
              appliedDateMode={filters.date_mode ?? REPORT_DATE_MODE.CUSTOM}
              initialFilters={{
                start_date: filters.start_date,
                end_date: filters.end_date,
                ...datePickerBounds,
              }}
              now={organizationNow}
              onApply={({ start_date, end_date, date_mode }) =>
                updateFilters({ start_date, end_date, date_mode, page: 1 })
              }
            />
            <AdminHeaderActionButton
              type="button"
              variant="outline"
              size="lg"
              onClick={handleExport}
              disabled={exportMutation.isPending}
            >
              <Download className="size-4" />
              {exportMutation.isPending ? 'Preparing…' : 'Export Spreadsheet'}
            </AdminHeaderActionButton>
          </div>
        }
      />

      {filters.sales_order_id ? (
        <InfoNotice
          compact
          tone="neutral"
          title={`Showing deposits for Order #${filters.sales_order_id}`}
        >
          <div className="flex flex-wrap items-center gap-3">
            <span>The date range includes every known deposit connected to this order.</span>
            <Can do="view" on="sales-orders">
              <Link
                {...path.salesOrders.show(filters.sales_order_id).using({ orgId }).link}
                className="font-medium text-interaction underline-offset-2 hover:underline"
              >
                Return to order
              </Link>
            </Can>
            <Button variant="link" size="inline" onClick={clearOrderFilter}>
              Clear order filter
            </Button>
          </div>
        </InfoNotice>
      ) : null}

      <DepositPipelineCard snapshot={currentBalance} />

      <Card>
        <CardHeader className="gap-3">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div className="space-y-1.5">
              <CardTitle>Deposits</CardTitle>
              <CardDescription>
                The period total covers completed deposits on every page. Collections and fees
                include refunds.
              </CardDescription>
            </div>
            <DepositStatusSelect
              value={filters.status ?? DEPOSIT_STATUS.ALL}
              onValueChange={(status) => updateFilters({ status, page: 1 })}
            />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <IndexDataTable
            data={tableRows}
            columns={getDepositColumns({
              path,
              orgId,
              salesOrderId: filters.sales_order_id,
              showPayoutFees: tableRows.some((row) => Number(row.payout_fees_minor ?? 0) !== 0),
              showInstantPayouts: tableRows.some(
                (row) => Number(row.instant_payouts_minor ?? 0) !== 0
              ),
              showManualPayouts: tableRows.some(
                (row) => Number(row.manual_payouts_minor ?? 0) !== 0
              ),
              showDisputes: tableRows.some((row) => Number(row.dispute_activity_minor ?? 0) !== 0),
              showOtherActivity: tableRows.some(
                (row) => Number(getOtherActivityMinor(row) ?? 0) !== 0
              ),
            })}
            getRowId={(row) => String(row.id)}
            getRowPresentation={getDepositRowPresentation}
            pagination={false}
            emptyState={
              <EmptyState
                size="compact"
                title="No deposits match these filters"
                description="Try a different date range or deposit status."
              />
            }
          />

          <IndexTablePagination
            meta={meta}
            pageSize={filters.page_size ?? 20}
            onPageChange={(page) => updateFilters({ page })}
            onPageSizeChange={(pageSize) => updateFilters({ page_size: pageSize, page: 1 })}
            summary={`${meta.total} ${meta.total === 1 ? 'deposit' : 'deposits'}${isFetching ? ' · updating' : ''}`}
          />
        </CardContent>
      </Card>
    </div>
  )
}

function DepositStatusSelect({ value, onValueChange }) {
  return (
    <div className="min-w-48 space-y-1">
      <Typography variant="caption" as="label">
        Deposit status
      </Typography>
      <Select value={value} onValueChange={onValueChange}>
        <SelectTrigger aria-label="Deposit status" className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {DEPOSIT_STATUS_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

function getDepositRowPresentation(deposit) {
  if (['failed', 'canceled'].includes(deposit.status)) return { tone: 'destructive' }
  if (deposit.needs_review) return { tone: 'warning' }

  return { tone: 'neutral' }
}

function getReportFilters(search, timezone) {
  return getDepositsReportFiltersFromSearch(
    search,
    getCurrentReportDateFilters(getOrganizationNow(timezone))
  )
}
